import React, { useState, useRef } from "react";
import axios from "axios";
import {
  Card,
  Flex,
  Group,
  SegmentedControl,
  Text,
  TextInput,
  Collapse,
  ActionIcon,
  Stack,
  TagsInput,
  Box,
} from "@mantine/core";
import {
  IconPencil,
  IconX,
  IconHash,
  IconUser,
  IconCake,
  IconEdit,
  IconSearch,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";

import { PaginatedList, ActionBar } from "@components/shared";
import { UserGroupDetail, UserGroupVisibility } from "./";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { numberFormatter } from "@util/formatters";
import { Taggit, TagPill } from "@components/Tag";

export default function UserGroupList({
  requestInfo = {
    url: `/segmentation/retrieve-user-groups/`,
    data: {},
  },
  locationId,
  orgId,
  effortId,
}) {
  const [filters, setFilters] = useState({
    // disposition: "everything",
    // variety: "entire",
    context: "entire",
    visibility: "entire",
  });
  const [searchValue, setSearchValue] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [refresh, setRefresh] = useState(false);

  return (
    <div>
      <ActionBar color={entityColor.user} containerProps={{ mb: "sm" }}>
        <Flex gap="xs" align="center">
          <IconSearch size={18} />
          <div>
            <Text size="md" fw={600}>
              Find segments
            </Text>
            <Text size="sm">
              Search by name or filter by tags. You can use both together.
            </Text>
          </div>
        </Flex>
      </ActionBar>
      <Flex gap="xs" mb="sm">
        <TextInput
          placeholder="Search by segment name"
          size="md"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ flexGrow: 1, width: "50%" }}
        />
        <Box style={{ flexGrow: 1, width: "50%" }}>
          <UserGroupTagFilter
            locationId={locationId}
            orgId={orgId}
            selectedTags={selectedTags}
            onChange={setSelectedTags}
          />
        </Box>
      </Flex>
      <Flex gap="sm" direction={{ base: "column", sm: "row" }} wrap="wrap">
        <SegmentedControl
          value={filters.context}
          data={[
            { label: "All", value: "entire" },
            { label: "Created from snapshots", value: "snapshots" },
            { label: "Created from efforts", value: "campaign_efforts" },
          ]}
          onChange={(e) =>
            setFilters({
              ...filters,
              context: e,
            })
          }
        />
        <SegmentedControl
          value={filters.visibility}
          data={[
            { label: "All", value: "entire" },
            { label: "Private", value: "private" },
            { label: "Public", value: "public" },
          ]}
          onChange={(e) =>
            setFilters({
              ...filters,
              visibility: e,
            })
          }
        />
      </Flex>
      <PaginatedList
        columns={1}
        requestInfo={{
          url: requestInfo.url,
          data: {
            ...requestInfo.data,
            ...filters,
            location_id: locationId,
            organization_id: orgId,
            search_value: searchValue,
            tag_ids: selectedTags.map((tag) => tag.id),
            campaign_effort_id: effortId,
            refresh: refresh,
          },
        }}
        responseMapping={(r) =>
          r.map((m) => ({
            ...m,
          }))
        }
        displayFormatter={({ original }) => (
          <Item
            key={original.id}
            original={original}
            fetchData={() => setRefresh(!refresh)}
            entityData={{
              location_id: locationId,
              organization_id: orgId,
            }}
          />
        )}
      />
    </div>
  );
}

function UserGroupTagFilter({
  locationId,
  orgId,
  selectedTags,
  onChange,
  ...props
}) {
  const [options, setOptions] = useState([]);
  const cancelRef = useRef(null);

  function searchTags(searchValue) {
    const cancel = cancelRef.current;
    if (cancel) cancel();

    if (!searchValue) {
      setOptions([]);
      return;
    }

    const req = {
      user_group_tags: true,
      value: searchValue,
      ...(locationId && { location_id: locationId }),
      ...(orgId && { organization_id: orgId }),
    };

    axios
      .post(`/search-all-tags/`, req, {
        cancelToken: new axios.CancelToken(function executor(c) {
          cancelRef.current = c;
        }),
      })
      .then(({ data }) => {
        const uniqueTags = data.response.reduce((acc, tag) => {
          if (!acc.some((item) => item.label === tag.name)) {
            acc.push({ label: tag.name, value: tag.id });
          }
          return acc;
        }, []);
        setOptions(uniqueTags);
      })
      .catch(() => {
        setOptions([]);
      });
  }

  function handleChange(tagNames) {
    const nextTags = tagNames.reduce((acc, name) => {
      const existing = selectedTags.find((tag) => tag.name === name);
      if (existing) {
        acc.push(existing);
        return acc;
      }

      const option = options.find((item) => item.label === name);
      if (option) {
        acc.push({ id: option.value, name: option.label });
      }

      return acc;
    }, []);

    onChange(nextTags);
  }

  return (
    <TagsInput
      placeholder="tags"
      value={selectedTags.map((tag) => tag.name)}
      onChange={handleChange}
      data={options.map((item) => item.label)}
      searchable
      onSearchChange={searchTags}
      size="md"
      leftSection={<IconHash size={16} />}
      {...props}
    />
  );
}

export const Item = ({ original, fetchData, entityData, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const editable = original.can_edit === true;

  return (
    <Card>
      <Flex
        gap="sm"
        align={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
      >
        <div style={{ flexGrow: 1 }}>
          <Stack gap={4}>
            <Flex gap="xs" align="center">
              <Text
                fw={700}
                lineClamp={1}
                component={Link}
                to={`/segments/${original.id}`}
              >
                {original.name}
              </Text>
              {/* {editable && (
                <ActionIcon
                  variant="light"
                  size="sm"
                  radius="xl"
                  color={isOpen ? "red" : "gray"}
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {isOpen ? <IconX size={12} /> : <IconPencil size={12} />}
                </ActionIcon>
              )} */}
              {children && <>{children}</>}
            </Flex>
            <Text maw={600} size="sm" lineClamp={1} mb={4}>
              {original.description}
            </Text>
            {(original.location_id || original.organization_id) && (
              <>
                <Group gap="xs" align="center" wrap="nowrap">
                  <div>
                    {original.location_id
                      ? entityIcon.location()
                      : entityIcon.organization()}
                  </div>
                  <Text size="sm" lineClamp={1}>
                    {original.location_name
                      ? original.location_name
                      : original.organization_name}
                  </Text>
                </Group>
              </>
            )}
            {original.campaign_effort_title && (
              <Group gap="xs" align="center" wrap="nowrap">
                <>{entityIcon.effort(14)}</>
                <Text
                  size="sm"
                  lineClamp={1}
                  title={original.campaign_effort_title || original.title}
                >
                  {original.campaign_effort_title}
                  {original.title ? ` (${original.title})` : ""}
                </Text>
              </Group>
            )}
            {original.snapshot_title && (
              <Group gap="xs" align="center" wrap="nowrap">
                <>{entityIcon.snapshot(14)}</>
                <Text lineClamp={1} size="sm">
                  {original.snapshot_title}
                </Text>
              </Group>
            )}
            {original.created_at && (
              <Group gap="xs" align="center" wrap="nowrap">
                <>
                  <IconCake size={14} />
                </>
                <Text size="sm" lineClamp={1}>
                  created {new Date(original.created_at).toLocaleString()}{" "}
                  {original.created_by && ` by ${original.created_by}`}
                </Text>
              </Group>
            )}
            {original.last_edited_at && original.updated_by && (
              <Group gap="xs" align="center" wrap="nowrap">
                <>
                  <IconEdit size={14} />
                </>
                <Text size="sm" lineClamp={1}>
                  last edited{" "}
                  {new Date(original.last_edited_at).toLocaleString()}{" "}
                  {original.updated_by && ` by ${original.updated_by}`}
                </Text>
              </Group>
            )}
            {original.user_count !== undefined && (
              <Group gap="xs" align="center" wrap="nowrap">
                <IconUser size={16} />
                <Text size="sm">
                  {numberFormatter(original.user_count)} user
                  {original.user_count === 1 ? "" : "s"}
                </Text>
              </Group>
            )}
            {((original.tags && original.tags.length > 0) ||
              (isOpen && editable)) && (
              <Group gap="xs" align="center" wrap="nowrap" mt={4}>
                <>{entityIcon.tag(14)}</>
                <div style={{ flexGrow: 1 }}>
                  {isOpen && editable ? (
                    <Taggit
                      reqInfo={{ user_group_id: original.id }}
                      fetchData={fetchData}
                    />
                  ) : (
                    <Flex gap="xs" wrap="wrap" align="center">
                      {original.tags.map((tag, index) => (
                        <TagPill tag={tag} key={`${tag.id}-${index}`} />
                      ))}
                    </Flex>
                  )}
                </div>
              </Group>
            )}
          </Stack>
        </div>
      </Flex>
      <Collapse
        keepMounted={false}
        in={isOpen}
        mt={isOpen ? (editable ? "xl" : "lg") : 0}
      >
        {isOpen && (
          <UserGroupDetail
            id={original.id}
            info={original}
            fetchData={fetchData}
            entityData={entityData}
            editable={editable}
          />
        )}
      </Collapse>
    </Card>
  );
};
